<template>
  <div class="checkout-address">
    <mdc-grid-container>
      <mdc-grid>
        <mdc-grid-cell
          v-if="show('firstName')"
          :cols="6"
        >
          <mdc-text-field
            v-model="value.firstName"
            label="First Name"
            name="fname"
            autocomplete="given-name"
            required
            @input="onAddressFieldChange('firstName', $event)"
            @validity="onAddressValidityChange('firstName', $event)"
          />
        </mdc-grid-cell>
        <mdc-grid-cell
          v-if="show('lastName')"
          :cols="6"
        >
          <mdc-text-field
            v-model="value.lastName"
            label="Last Name"
            name="lname"
            autocomplete="family-name"
            required
            @input="onAddressFieldChange('lastName', $event)"
            @validity="onAddressValidityChange('lastName', $event)"
          />
        </mdc-grid-cell>
        <mdc-grid-cell
          v-if="show('company')"
          :cols="12"
        >
          <mdc-text-field
            v-model="value.company"
            label="Company (optional)"
            name="comany"
            autocomplete="company"
            @input="onAddressFieldChange('company', $event)"
            @validity="onAddressValidityChange('company', $event)"
          />
        </mdc-grid-cell>
        <mdc-grid-cell :cols="12">
          <mdc-text-field
            id="address"
            v-model="value.line1"
            label="Address"
            name="address-line1"
            :autocomplete="autocomplete('address-line1')"
            required
            @input="onAddressFieldChange('line1', $event)"
            @validity="onAddressValidityChange('line1', $event)"
          />
        </mdc-grid-cell>
        <mdc-grid-cell :cols="12">
          <mdc-text-field
            v-model="value.line2"
            label="Apartment, suite, etc (optional)"
            name="address_line2"
            :autocomplete="autocomplete('address-line2')"
            @input="onAddressFieldChange('line2', $event)"
            @validity="onAddressValidityChange('line2', $event)"
          />
        </mdc-grid-cell>
        <mdc-grid-cell :cols="12">
          <mdc-text-field
            v-model="value.city"
            label="City"
            name="city"
            :autocomplete="autocomplete('address-level2')"
            required
            @input="onAddressFieldChange('city', $event)"
            @validity="onAddressValidityChange('city', $event)"
          />
        </mdc-grid-cell>
        <mdc-grid-cell :layout="responsiveLayout">
          <mdc-select-field
            v-model="value.country"
            label="Country"
            name="country"
            id-key="alpha3"
            value-key="alpha3"
            label-key="name"
            required
            :options="countries"
            @input="onAddressFieldChange('country', $event)"
          />
        </mdc-grid-cell>
        <mdc-grid-cell :layout="responsiveLayout">
          <!-- <mdc-text-field
            v-model="value.state"
            label="State"
            name="state"
            :autocomplete="autocomplete('address-level1')"
            required
            @input="onAddressFieldChange('state', $event)"
            @validity="onAddressValidityChange('state', $event)"
          /> -->
          <mdc-select-field
            v-model="value.state"
            label="State"
            name="state"
            id-key="alpha3"
            value-key="alpha3"
            label-key="name"
            :disabled="subdivisions.length === 0"
            :required="subdivisions.length > 0"
            :options="subdivisions"
            @input="onAddressFieldChange('state', $event)"
          />
        </mdc-grid-cell>
        <mdc-grid-cell :layout="responsiveLayout">
          <mdc-text-field
            v-model="value.zip"
            label="Zip"
            name="zip"
            :autocomplete="autocomplete('postal-code')"
            required
            @input="onAddressFieldChange('zip', $event)"
            @validity="onAddressValidityChange('zip', $event)"
          />
        </mdc-grid-cell>
        <mdc-grid-cell
          v-if="show('phone')"
          :cols="12"
        >
          <mdc-text-field
            v-model="value.phone"
            label="Phone"
            type="tel"
            name="phone"
            autocomplete="tel"
            required
            :validate="validatePhone"
            @input="onAddressFieldChange('zip', $event)"
            @validity="onAddressValidityChange('zip', $event)"
          />
        </mdc-grid-cell>
      </mdc-grid>
    </mdc-grid-container>
  </div>
</template>

<script>
import * as _ from 'lodash'
import * as axios from 'axios'
import MdcGrid from '../material/Grid'
import MdcGridCell from '../material/GridCell'
import MdcGridContainer from '../material/GridContainer'
import MdcTextField from '../material/TextField'
import MdcSelectField from '../material/SelectField'
import loadGoogleMaps from '../../lib/google_maps'
import { countries, getSubdivisionsByCountry } from './iso_3166'
import { isPostalCode } from 'validator'

const twilio = axios.create({
  baseURL: 'https://flame-cuscus-3593.twil.io'
})

export function address () {
  return {
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  }
}

export default {
  components: {
    MdcGrid,
    MdcGridCell,
    MdcGridContainer,
    MdcTextField,
    MdcSelectField
  },
  props: {
    value: {
      type: Object,
      default () {
        return address()
      }
    },
    type: {
      type: String,
      default: ''
    },
    disabledFields: {
      type: Array,
      default () { return [] }
    },
    googleMapsApiKey: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      countries,
      responsiveLayout: [
        { device: 'phone', columns: 4 },
        { device: 'tablet', columns: 2 }
      ]
    }
  },
  computed: {
    subdivisions () {
      return getSubdivisionsByCountry(this.value.country)
    }
  },
  async mounted () {
    this.google = await loadGoogleMaps({
      key: this.googleMapsApiKey
    })

    this.googleMapsAutocomplete = new this.google.maps.places.Autocomplete(this.$el.querySelector('#address'))

    this.googleMapsAutocomplete.addListener('place_changed', () => {
      this.onPlaceChanged(this.googleMapsAutocomplete.getPlace())
    })
  },
  methods: {
    autocomplete (field) {
      return `${this.type} ${field}`.trim()
    },
    show (field) {
      return this.disabledFields.includes(field) === false
    },
    validateZIP (zip) {
      return {
        valid: !!zip && isPostalCode(zip, 'any'),
        message: 'Invalid zip code'
      }
    },
    onAddressFieldChange (field, value) {
      this.$emit('input', this.value)
      if (field === 'country') {
        this.value.state = ''
      }
    },
    onAddressValidityChange (field, { valid, message }) {
      this.$emit('validity', { field, valid, message })
    },
    onPlaceChanged (place) {
      const component = this.componentGetter(place.address_components)
      const streedAddress = [
        component({ type: 'street_number' }),
        component({ type: 'route' })
      ].join(' ').trim()

      const city = component({ type: 'locality' })
      const countryAlpha2 = component({ type: 'country' })
      const country = _.chain(countries).find(c => c.alpha2 === countryAlpha2).get('alpha3').value()
      const stateAlpha2 = component({ type: 'administrative_area_level_1' })
      const subdivisions = getSubdivisionsByCountry(country)
      const state = _.chain(subdivisions).find(s => s.alpha3 === `${countryAlpha2}-${stateAlpha2}`).get('alpha3').value()
      const zip = component({ type: 'postal_code' })

      this.country = country

      this.$el.querySelector('#address').value = streedAddress

      _.assign(this.value, {
        line1: streedAddress,
        city,
        country,
        state,
        zip
      })

      this.$emit('input', this.value)
    },
    componentGetter (components) {
      return ({ type, key = 'short_name', defaultValue = '' }) => {
        return _.chain(components).find(c => c.types.includes(type)).get(key, defaultValue).value()
      }
    },

    async validatePhone (phoneNumber) {
      if (!phoneNumber) {
        return { valid: true, message: '' }
      }

      try {
        const { data } = await twilio.get('/lookup', {
          params: {
            number: phoneNumber
          }
        })

        this.value.phone = data.phoneNumber
        this.$emit('input', this.value)
        return { valid: true, message: '' }
      } catch (err) {
        console.error('phoneValidationError', err)
        return {
          valid: false,
          message: 'Invalid Phone Number'
        }
      }
    }
  }
}
</script>

<style lang="scss">
</style>
